import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser, logoutUser } from '../lib/auth';
import toast from 'react-hot-toast';
import {
  FaUser, FaLock, FaTrash, FaCamera, FaArrowLeft, FaEdit,
  FaSave, FaTimes, FaEye, FaEyeSlash, FaSpinner, FaExclamationTriangle,
  FaShieldAlt, FaEnvelope
} from 'react-icons/fa';
import ProfileImageUpload from '../components/ProfileImageUpload';
import bcrypt from 'bcryptjs';

interface ProfileData {
  id: string;
  email: string;
  full_name: string | null;
  username: string | null;
  profile_image: string | null;
  created_at: string;
  is_featured: boolean;
  wallet_balance: number;
}

export default function Profile() {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const userId = currentUser?.id ?? null;

  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);

  // edit toggles
  const [editingName, setEditingName] = useState(false);
  const [editingUsername, setEditingUsername] = useState(false);

  // form states
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // modal toggles
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // ui states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [savingUsername, setSavingUsername] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  // Redirect if not logged in & load once per userId
  useEffect(() => {
    if (!userId) {
      navigate('/login');
      return;
    }
    loadProfileData(userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, navigate]);

  // Initialize form fields from profileData when NOT editing
  useEffect(() => {
    if (!profileData) return;
    if (!editingName) setFullName(profileData.full_name || '');
    if (!editingUsername) setUsername(profileData.username || '');
  }, [profileData, editingName, editingUsername]);

  // Debounced username availability check (normalize only for the query)
  useEffect(() => {
    if (!userId) return;

    const normalizedInput = username.trim().toLowerCase();
    const normalizedCurrent = (profileData?.username ?? '').toLowerCase();

    if (!normalizedInput || normalizedInput === normalizedCurrent) {
      setUsernameAvailable(null);
      return;
    }

    const check = async () => {
      setCheckingUsername(true);
      try {
        const { data, error } = await supabase
          .from('stripeusers')
          .select('id')
          .eq('username', normalizedInput)
          .neq('id', userId)
          .maybeSingle();

        if (error && (error as any).code !== 'PGRST116') throw error;
        setUsernameAvailable(!data);
      } catch (err) {
        console.error('Error checking username:', err);
        setUsernameAvailable(null);
      } finally {
        setCheckingUsername(false);
      }
    };

    const id = setTimeout(check, 500);
    return () => clearTimeout(id);
  }, [username, userId, profileData?.username]);

  async function loadProfileData(id: string) {
    setLoading(true);
    let retryCount = 0;
    const maxRetries = 3;

    const attemptLoad = async (): Promise<void> => {
      try {
        const { data, error } = await supabase
          .from('stripeusers')
          .select('id, email, full_name, username, profile_image, created_at, is_featured, wallet_balance')
          .eq('id', id)
          .single();

        if (error) throw error;
        setProfileData(data as ProfileData);
      } catch (error: any) {
        console.error('Error loading profile (attempt ' + (retryCount + 1) + '):', error);

        if (retryCount < maxRetries && (error?.message?.includes('Failed to fetch') || error?.name === 'TypeError')) {
          retryCount++;
          await new Promise(resolve => setTimeout(resolve, 2000));
          return attemptLoad();
        }

        if (error?.message?.includes('Failed to fetch')) {
          toast.error('Network connection issue. Please check your internet and try again.');
        } else {
          toast.error('Failed to load profile data. Please refresh the page.');
        }

        setTimeout(() => navigate('/dashboard'), 3000);
      }
    };

    try {
      await attemptLoad();
    } finally {
      setLoading(false);
    }
  }

  const handleUpdateFullName = async () => {
    if (!userId || !profileData) return;

    setSavingName(true);
    try {
      const clean = fullName.trim() || null;
      const { error } = await supabase
        .from('stripeusers')
        .update({ full_name: clean })
        .eq('id', userId);

      if (error) throw error;

      setProfileData({ ...profileData, full_name: clean });
      setEditingName(false);
      toast.success('Full name updated successfully');

      const updatedUser = { ...currentUser, full_name: clean };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error: any) {
      console.error('Error updating full name:', error);
      toast.error('Failed to update full name');
    } finally {
      setSavingName(false);
    }
  };

  const handleUpdateUsername = async () => {
    if (!userId || !profileData) return;

    const normalized = username.trim().toLowerCase();

    if (normalized && normalized.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    if (normalized && !/^[a-z0-9_-]+$/.test(normalized)) {
      toast.error('Username can only contain letters, numbers, underscores, and hyphens');
      return;
    }
    if (usernameAvailable === false && normalized !== (profileData.username ?? '')) {
      toast.error('Username is already taken');
      return;
    }

    setSavingUsername(true);
    try {
      const clean = normalized || null;
      const { error } = await supabase
        .from('stripeusers')
        .update({ username: clean })
        .eq('id', userId);

      if (error) throw error;

      setProfileData({ ...profileData, username: clean });
      setEditingUsername(false);
      toast.success('Username updated successfully');

      const updatedUser = { ...currentUser, username: clean };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error: any) {
      console.error('Error updating username:', error);
      if (error?.message?.includes('Failed to fetch')) {
        toast.error('Network error. Please check your connection and try again.');
      } else if (error?.code === '23505') {
        toast.error('Username is already taken');
      } else {
        toast.error('Failed to update username');
      }
    } finally {
      setSavingUsername(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !profileData) return;

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      const { data: userData, error: fetchError } = await supabase
        .from('stripeusers')
        .select('password_hash')
        .eq('id', userId)
        .single();

      if (fetchError) throw fetchError;
      if (!userData?.password_hash) {
        toast.error('Password update unavailable for this account.');
        return;
      }

      const isCurrentPasswordValid = bcrypt.compareSync(currentPassword, userData.password_hash);
      if (!isCurrentPasswordValid) {
        toast.error('Current password is incorrect');
        return;
      }

      const newPasswordHash = bcrypt.hashSync(newPassword, 10);
      const { error } = await supabase
        .from('stripeusers')
        .update({ password_hash: newPasswordHash })
        .eq('id', userId);

      if (error) throw error;

      toast.success('Password changed successfully');
      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Error changing password:', error);
      if (error?.message?.includes('Failed to fetch')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error('Failed to change password');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!userId || deleteConfirmText !== 'DELETE') return;

    setDeletingAccount(true);
    try {
      const { error } = await supabase
        .from('stripeusers')
        .delete()
        .eq('id', userId);

      if (error) throw error;

      toast.success('Account deleted successfully');
      await logoutUser(userId);
      navigate('/');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      if (error?.message?.includes('Failed to fetch')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error('Failed to delete account');
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleImageUpdate = (imageUrl: string) => {
    if (profileData) {
      setProfileData({ ...profileData, profile_image: imageUrl });
      setShowImageUpload(false);
      const updatedUser = { ...currentUser, profile_image: imageUrl };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-yellow-400 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-lg">Profile not found</p>
          <Link to="/dashboard" className="text-yellow-600 hover:text-yellow-700 mt-2 inline-block">
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link
              to="/dashboard"
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors duration-200"
            >
              <div className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200">
                <FaArrowLeft className="w-4 h-4" />
              </div>
              <span className="font-medium">Back to Dashboard</span>
            </Link>
          </div>

          {profileData.is_featured && (
            <div className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black rounded-full font-bold shadow-lg">
              <FaShieldAlt className="w-4 h-4" />
              <span>Featured Member</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Overview Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 p-6 text-center">
                <div className="relative inline-block">
                  <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-lg mx-auto">
                    {profileData.profile_image ? (
                      <img src={profileData.profile_image} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-600 font-bold text-2xl">
                        {profileData.email.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setShowImageUpload(true)}
                    className="absolute -bottom-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    <FaCamera className="w-4 h-4 text-gray-600" />
                  </button>
                </div>

                <h1 className="text-2xl font-bold text-black mt-4">
                  {profileData.username || profileData.full_name || 'User Profile'}
                </h1>
                <p className="text-black/70">{profileData.email}</p>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Member since</span>
                  <span className="font-medium text-gray-900">{formatDate(profileData.created_at)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Wallet Balance</span>
                  <span className="font-bold text-green-600">${Number(profileData.wallet_balance || 0).toFixed(2)}</span>
                </div>
                {profileData.is_featured && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Status</span>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                      Featured Member
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Profile Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <FaUser className="w-5 h-5 text-yellow-500 mr-3" />
                  Personal Information
                </h2>
              </div>

              <div className="p-6 space-y-6">
                {/* Email (Read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <FaEnvelope className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-900 font-medium">{profileData.email}</span>
                    <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">Read-only</span>
                  </div>
                </div>

                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  {editingName ? (
                    <div className="flex items-center space-x-3">
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Enter your full name"
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                        maxLength={100}
                        autoFocus
                        autoCapitalize="words"
                        autoCorrect="on"
                        spellCheck={false}
                      />
                      <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleUpdateFullName}
                        disabled={savingName}
                        className="p-3 bg-green-500 hover:bg-green-600 disabled:opacity-50 rounded-xl text-white transition-colors duration-200"
                      >
                        {savingName ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaSave className="w-4 h-4" />}
                      </button>
                      <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setEditingName(false);
                          setFullName(profileData.full_name || '');
                        }}
                        className="p-3 bg-gray-500 hover:bg-gray-600 rounded-xl text-white transition-colors duration-200"
                      >
                        <FaTimes className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <span className="text-gray-900 font-medium">
                        {profileData.full_name || 'Not set'}
                      </span>
                      <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setEditingName(true)}
                        className="p-2 bg-yellow-500 hover:bg-yellow-600 rounded-lg text-white transition-colors duration-200"
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  {editingUsername ? (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}  // no transforms while typing
                            placeholder="Enter a unique username"
                            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white text-gray-900 placeholder-gray-500 ${
                              usernameAvailable === false ? 'border-red-300' :
                                usernameAvailable === true ? 'border-green-300' : 'border-gray-300'
                            }`}
                            maxLength={30}
                            inputMode="text"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            autoComplete="username"
                            autoFocus
                          />
                          {checkingUsername && (
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                              <FaSpinner className="w-4 h-4 text-gray-400 animate-spin" />
                            </div>
                          )}
                        </div>
                        <button
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleUpdateUsername}
                          disabled={savingUsername || usernameAvailable === false || checkingUsername}
                          className="p-3 bg-green-500 hover:bg-green-600 disabled:opacity-50 rounded-xl text-white transition-colors duration-200"
                        >
                          {savingUsername ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaSave className="w-4 h-4" />}
                        </button>
                        <button
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setEditingUsername(false);
                            setUsername(profileData.username || '');
                            setUsernameAvailable(null);
                          }}
                          className="p-3 bg-gray-500 hover:bg-gray-600 rounded-xl text-white transition-colors duration-200"
                        >
                          <FaTimes className="w-4 h-4" />
                        </button>
                      </div>

                      {username && username.toLowerCase() !== (profileData.username ?? '').toLowerCase() && (
                        <div className="text-sm">
                          {checkingUsername ? (
                            <p className="text-gray-500">Checking availability...</p>
                          ) : usernameAvailable === true ? (
                            <p className="text-green-600">✓ Username is available</p>
                          ) : usernameAvailable === false ? (
                            <p className="text-red-600">✗ Username is already taken</p>
                          ) : null}
                        </div>
                      )}

                      <p className="text-xs text-gray-500">
                        Username must be 3-30 characters and can only contain letters, numbers, underscores, and hyphens.
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <span className="text-gray-900 font-medium">
                        {profileData.username || 'Not set'}
                      </span>
                      <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setEditingUsername(true)}
                        className="p-2 bg-yellow-500 hover:bg-yellow-600 rounded-lg text-white transition-colors duration-200"
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Security Settings */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <FaLock className="w-5 h-5 text-blue-500 mr-3" />
                  Security Settings
                </h2>
              </div>

              <div className="p-6">
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setShowPasswordChange(true);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="w-full flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 rounded-xl border border-blue-200 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-3">
                    <FaLock className="w-5 h-5 text-blue-500" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">Change Password</p>
                      <p className="text-sm text-gray-600">Update your account password</p>
                    </div>
                  </div>
                  <FaEdit className="w-4 h-4 text-blue-500" />
                </button>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-white rounded-2xl shadow-lg border border-red-200 overflow-hidden">
              <div className="p-6 border-b border-red-200 bg-red-50">
                <h2 className="text-xl font-bold text-red-900 flex items-center">
                  <FaExclamationTriangle className="w-5 h-5 text-red-500 mr-3" />
                  Danger Zone
                </h2>
              </div>

              <div className="p-6">
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full flex items-center justify-between p-4 bg-red-50 hover:bg-red-100 rounded-xl border border-red-200 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-3">
                    <FaTrash className="w-5 h-5 text-red-500" />
                    <div className="text-left">
                      <p className="font-medium text-red-900">Delete Account</p>
                      <p className="text-sm text-red-600">Permanently delete your account and all data</p>
                    </div>
                  </div>
                  <FaTrash className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Image Upload Modal */}
      {showImageUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Update Profile Picture</h3>
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setShowImageUpload(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            <ProfileImageUpload
              userId={profileData.id}
              currentImage={profileData.profile_image}
              onImageUpdate={handleImageUpdate}
            />
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Change Password</h3>
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setShowPasswordChange(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-gray-900 placeholder-gray-500"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showCurrentPassword ? <FaEyeSlash className="w-4 h-4 text-gray-400" /> : <FaEye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-gray-900 placeholder-gray-500"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showNewPassword ? <FaEyeSlash className="w-4 h-4 text-gray-400" /> : <FaEye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-gray-900 placeholder-gray-500"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showConfirmPassword ? <FaEyeSlash className="w-4 h-4 text-gray-400" /> : <FaEye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setShowPasswordChange(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-700 font-medium transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={changingPassword || newPassword !== confirmPassword}
                  className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-white font-medium transition-colors duration-200 flex items-center justify-center"
                >
                  {changingPassword ? (
                    <>
                      <FaSpinner className="w-4 h-4 animate-spin mr-2" />
                      Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaExclamationTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Delete Account</h3>
              <p className="text-gray-600">This action cannot be undone. All your data will be permanently deleted.</p>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-800 mb-2">What will be deleted:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Your profile and account information</li>
                  <li>• All your messages and conversations</li>
                  <li>• Your wallet balance and transaction history</li>
                  <li>• Your learning progress and achievements</li>
                  <li>• All uploaded content and files</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type "DELETE" to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-400 focus:border-transparent text-gray-900 placeholder-gray-500"
                />
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                  }}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-700 font-medium transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleDeleteAccount}
                  disabled={deletingAccount || deleteConfirmText !== 'DELETE'}
                  className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-white font-medium transition-colors duration-200 flex items-center justify-center"
                >
                  {deletingAccount ? (
                    <>
                      <FaSpinner className="w-4 h-4 animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FaTrash className="w-4 h-4 mr-2" />
                      Delete Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
